import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { SeoHead } from '../components/SeoHead';
import { SEO } from '../config/seo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  DollarSign, TrendingUp, Users, Award, CheckCircle2, Zap, Gift, 
  BarChart, Clock, AlertCircle, Star, ArrowRight, Briefcase, 
  Target, Shield, CreditCard, Globe, ChevronRight, UserPlus,
  Mail, Video, MessageSquare, Sparkles, Bell
} from 'lucide-react';


import { API_URL } from '../config';
const API = `${API_URL}/api`;

function AffiliateLanding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const partnerCode = searchParams.get('partner'); // Capture partner referral code from URL
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    niche: '',
    followerCount: '',
    profileUrl: '',
    promotionStrategy: '',
    website: '',
    acceptedTerms: false,
    referred_by_partner: partnerCode || ''
  });
  
  const [submitting, setSubmitting] = useState(false);

  // Update referred_by_partner if URL changes
  useEffect(() => {
    if (partnerCode) {
      setFormData(prev => ({ ...prev, referred_by_partner: partnerCode }));
    }
  }, [partnerCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (parseInt(formData.followerCount) < 10000) {
      alert('We require a minimum of 10,000 engaged followers.');
      return;
    }
    
    if (!formData.acceptedTerms) {
      alert('Please read and accept the Affiliate Program Terms & Conditions to continue.');
      return;
    }
    
    setSubmitting(true);

    try {
      await axios.post(`${API}/affiliate/apply`, formData);
      alert('Application submitted! You\'ll hear from us within 24 hours.');
      navigate('/affiliate/status');
    } catch (error) {
      console.error('Error:', error);
      alert('Error submitting application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: <DollarSign className="h-8 w-8 text-blue-600" />,
      title: "Up to 25% Recurring Commission",
      description: "Earn lifetime commissions on every referral. Your income grows as your referrals stay."
    },
    {
      icon: <TrendingUp className="h-8 w-8 text-blue-600" />,
      title: "High-Value Services",
      description: "Our services range from $299-$15,000+. Bigger deals = bigger commissions for you."
    },
    {
      icon: <UserPlus className="h-8 w-8 text-blue-600" />,
      title: "Earn 10% on Partner Referrals",
      description: "Recruit other affiliates and earn 10% of THEIR commissions. Build a team, earn passively!"
    },
    {
      icon: <Shield className="h-8 w-8 text-blue-600" />,
      title: "90-Day Cookie Duration",
      description: "Extended tracking ensures you get credit for referrals even weeks after the first click."
    },
    {
      icon: <CreditCard className="h-8 w-8 text-blue-600" />,
      title: "Fast Payouts via Stripe",
      description: "Get paid within 48 hours of milestone bonuses. Monthly commission payouts guaranteed."
    }
  ];

  const commissionTiers = [
    { tier: "Starter", range: "1-10", rate: "10%", perClient: "$30", maxPerClient: "$51", color: "slate" },
    { tier: "Growth", range: "11-25", rate: "15%", perClient: "$45", maxPerClient: "$76", color: "blue" },
    { tier: "Professional", range: "26-50", rate: "20%", perClient: "$60", maxPerClient: "$101", color: "blue" },
    { tier: "Elite Partner", range: "51+", rate: "25%", perClient: "$75", maxPerClient: "$127", color: "blue", featured: true, badge: "Top Earner" }
  ];

  const servicePackages = [
    { name: "Website Package", price: "$299/mo", commission10: "$30", commission25: "$75" },
    { name: "Google Business Suite", price: "$79/mo", commission10: "$8", commission25: "$20" },
    { name: "SEO Turbo Pro", price: "$129/mo", commission10: "$13", commission25: "$32" },
    { name: "SEO Enterprise", price: "$299/mo", commission10: "$30", commission25: "$75" },
    { name: "Google Reviews Manager", price: "$29/mo", commission10: "$3", commission25: "$7" },
  ];

  const earningsExamples = [
    { scenario: "5 clients on Website only", monthly: "$150", annual: "$1,800", tier: "Starter 10%" },
    { scenario: "15 clients on Website + Google Suite", monthly: "$1,134", annual: "$13,608", tier: "Growth 15%" },
    { scenario: "30 clients on Website + SEO Pro", monthly: "$2,568", annual: "$30,816", tier: "Professional 20%" },
    { scenario: "50+ clients with full stack", monthly: "$6,350+", annual: "$76,200+", tier: "Elite 25%" },
  ];

  const testimonials = [
    {
      quote: "The recurring commission model is a game-changer. I made one video and now earn $900/month passively.",
      name: "David Chen",
      role: "Auto Industry Blogger",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=100&h=100&q=80"
    },
    {
      quote: "Finally an affiliate program that respects influencers. Fast payments, great tracking, and real support.",
      name: "Sarah Martinez",
      role: "Digital Marketing Consultant",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=100&h=100&q=80"
    },
    {
      quote: "My audience needed AI visibility tools. UPost delivered. I earn $2,400/month from referrals.",
      name: "Michael Brooks",
      role: "Tech YouTuber",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=100&h=100&q=80"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <SeoHead {...SEO.affiliate} />

      {/* Partner Referral Banner - Shows when referred by existing affiliate */}
      {partnerCode && (
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-3 px-4 text-center">
          <div className="flex items-center justify-center gap-2">
            <UserPlus className="h-5 w-5" />
            <span className="font-medium">
              You've been invited by an existing partner! Apply now and start earning together.
            </span>
          </div>
        </div>
      )}

      {/* Hero Section - Fun Sloth Theme with Original Colors */}
      <section className="relative py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute top-10 right-10 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-6 bg-blue-500 text-white font-bold px-4 py-2 text-sm">
                The Laziest Way to Earn Online
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 leading-tight">
                <span className="text-white">So Easy, Even a</span>
                <span className="block text-cyan-400">Sleepy Sloth</span>
                <span className="block text-blue-400">Can Do It</span>
              </h1>
              <p className="text-xl text-slate-300 mb-4">
                Tired of affiliate programs that make you work harder than a caffeinated squirrel?
              </p>
              <p className="text-lg text-slate-400 mb-8">
                No membership fees. No sharing commissions with middlemen. No complicated funnels. 
                Just share your link, take a nap, and wake up to <strong className="text-white">real passive income</strong>.
              </p>
              
              {/* Comedic Points */}
              <div className="space-y-3 mb-8">
                <div className="flex items-center gap-3 text-slate-300">
                  <span className="text-2xl">🛋️</span>
                  <span><strong className="text-white">Hate the corporate grind?</strong> Work from your couch in your PJ's</span>
                </div>
                <div className="flex items-center gap-3 text-slate-300">
                  <span className="text-2xl">😴</span>
                  <span>Make money while literally doing nothing (our sloths approve)</span>
                </div>
                <div className="flex items-center gap-3 text-slate-300">
                  <span className="text-2xl">🚫</span>
                  <span>No ClickBank nonsense or hidden platform fees</span>
                </div>
                <div className="flex items-center gap-3 text-slate-300">
                  <span className="text-2xl">💰</span>
                  <span>Keep 100% of YOUR commission (revolutionary, we know)</span>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-blue-600 hover:bg-blue-500 text-white font-bold px-8 text-lg"
                  onClick={() => document.getElementById('apply').scrollIntoView({ behavior: 'smooth' })}
                >
                  Start Being Lazy & Rich <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-slate-400 text-white hover:bg-white/10"
                  onClick={() => document.getElementById('benefits').scrollIntoView({ behavior: 'smooth' })}
                >
                  See How Easy It Is
                </Button>
              </div>
              <p className="mt-4 text-slate-400">
                Already a partner? <Link to="/affiliate/dashboard" className="text-blue-400 hover:text-blue-300 underline font-medium">Login to Dashboard</Link>
              </p>
            </div>
            <div className="hidden lg:block space-y-4">
              {/* Real Sloth Image - Full Body Shot */}
              <Card className="bg-white/10 backdrop-blur-md border-white/20 overflow-hidden">
                <div className="relative">
                  <img 
                    src="https://images.unsplash.com/photo-1590536385345-602954f53f6c?auto=format&fit=crop&w=600&h=350&q=80"
                    alt="Cute sloth hanging from tree branch - representing easy passive income"
                    className="w-full h-44 object-cover object-center"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/20 to-transparent" />
                  <div className="absolute bottom-3 left-4 right-4">
                    <p className="text-white font-bold text-lg">"This is my financial advisor"</p>
                    <p className="text-blue-300 text-sm">- Our Spirit Animal 🦥</p>
                  </div>
                </div>
              </Card>
              
              {/* Work From Home in PJ's Image */}
              <Card className="bg-white/10 backdrop-blur-md border-white/20 overflow-hidden">
                <div className="relative">
                  <img 
                    src="https://images.unsplash.com/photo-1758521541409-256fa9e24fe4?auto=format&fit=crop&w=600&h=250&q=80"
                    alt="Happy woman working from home with laptop on bed"
                    className="w-full h-36 object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
                  <div className="absolute bottom-3 left-4 right-4">
                    <p className="text-white font-bold">"My commute is 10 steps to the couch"</p>
                    <p className="text-blue-300 text-sm">- Living the Dream</p>
                  </div>
                </div>
              </Card>
              
              {/* Earnings Calculator */}
              <Card className="bg-white/10 backdrop-blur-md border-white/20">
                <CardContent className="p-4">
                  <h3 className="text-lg font-bold mb-3 text-center text-white">Your Earning Potential</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center p-2 bg-white/10 rounded-lg">
                      <span className="text-slate-300 text-sm">10 Referrals</span>
                      <span className="text-lg font-bold text-blue-400">$300/mo</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-white/10 rounded-lg">
                      <span className="text-slate-300 text-sm">25 Referrals</span>
                      <span className="text-lg font-bold text-blue-400">$1,125/mo</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-blue-600/30 rounded-lg border-2 border-blue-400">
                      <span className="text-white font-bold text-sm">50+ Referrals</span>
                      <span className="text-xl font-black text-white">$3,750+/mo</span>
                    </div>
                  </div>
                  <p className="text-center text-slate-400 text-xs mt-3">*Pants optional. Results may vary.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar - With Humor */}
      <section className="bg-slate-900 border-b border-slate-700 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-3xl font-bold text-blue-400">25%</p>
              <p className="text-slate-400 text-sm">Max Commission</p>
              <p className="text-xs text-slate-500">(That's ALL yours)</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-blue-400">90 Days</p>
              <p className="text-slate-400 text-sm">Cookie Duration</p>
              <p className="text-xs text-slate-500">(Plenty of nap time)</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-blue-400">$0</p>
              <p className="text-slate-400 text-sm">Membership Fees</p>
              <p className="text-xs text-slate-500">(Unlike those other guys)</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-blue-400">∞</p>
              <p className="text-slate-400 text-sm">Earning Potential</p>
              <p className="text-xs text-slate-500">(Sky's the limit)</p>
            </div>
          </div>
        </div>
      </section>

      {/* NEW: AI Marketing Assistant Feature Highlight */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <Badge className="mb-4 bg-blue-500 text-white font-bold">
                Included FREE for All Affiliates
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                AI Marketing Assistant
              </h2>
              <p className="text-lg text-slate-300 mb-6">
                Don't know what to say? Let AI do the heavy lifting! Our built-in marketing assistant 
                generates <strong className="text-white">custom content</strong> tailored to YOUR audience.
              </p>
              <p className="text-xl text-cyan-400 font-bold mb-6">
                So easy, even a sloth can do it!
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3 text-slate-300">
                  <Mail className="h-5 w-5 text-blue-400 flex-shrink-0" />
                  <span><strong className="text-white">Email Templates</strong> — Persuasive outreach that converts</span>
                </li>
                <li className="flex items-center gap-3 text-slate-300">
                  <Globe className="h-5 w-5 text-blue-400 flex-shrink-0" />
                  <span><strong className="text-white">Landing Page Ideas</strong> — Full page concepts ready to build</span>
                </li>
                <li className="flex items-center gap-3 text-slate-300">
                  <Video className="h-5 w-5 text-blue-400 flex-shrink-0" />
                  <span><strong className="text-white">Video Scripts</strong> — Hook-to-CTA scripts for any platform</span>
                </li>
                <li className="flex items-center gap-3 text-slate-300">
                  <MessageSquare className="h-5 w-5 text-blue-400 flex-shrink-0" />
                  <span><strong className="text-white">Social Media Posts</strong> — 5 posts for all major platforms</span>
                </li>
              </ul>
              <p className="text-sm text-slate-400 italic">
                "Just describe your audience, pick a tone, and click generate. That's it. We told you it was easy."
              </p>
            </div>
            <div className="hidden md:block">
              <Card className="bg-white/10 backdrop-blur-md border-white/20 overflow-hidden">
                <CardContent className="p-6">
                  <div className="text-center mb-4">
                    <div className="bg-blue-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Sparkles className="h-8 w-8 text-blue-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white">Generate in Seconds</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-white/10 rounded-lg p-3">
                      <p className="text-xs text-slate-400 mb-1">Step 1</p>
                      <p className="text-sm text-white">Describe your target audience</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-3">
                      <p className="text-xs text-slate-400 mb-1">Step 2</p>
                      <p className="text-sm text-white">Pick content type & tone</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-3">
                      <p className="text-xs text-slate-400 mb-1">Step 3</p>
                      <p className="text-sm text-white">Click generate & copy</p>
                    </div>
                    <div className="bg-blue-500/20 rounded-lg p-3 border border-blue-500/30">
                      <p className="text-xs text-blue-300 mb-1">Result</p>
                      <p className="text-sm text-white font-bold">Professional content, ready to use!</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Your Partner Toolkit Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-100 text-blue-700 font-bold">
              🛠️ Your Complete Partner Toolkit
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Everything You Need to Succeed
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We don't just give you a link and wish you luck. You get a full suite of tools to help you earn.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Custom Landing Pages */}
            <Card className="border-2 border-slate-200 hover:border-blue-300 transition-colors">
              <CardContent className="p-6">
                <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <Globe className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="font-bold text-slate-900 mb-2">Custom Landing Pages</h3>
                <p className="text-sm text-slate-600 mb-3">
                  Get your own branded landing page tailored to your niche.
                </p>
                <ul className="text-xs text-slate-500 space-y-1">
                  <li>✓ 8 industry templates</li>
                  <li>✓ Customize headlines & colors</li>
                  <li>✓ Built-in lead capture form</li>
                  <li>✓ Goes live at /p/YourCode</li>
                </ul>
              </CardContent>
            </Card>

            {/* Lead Notifications */}
            <Card className="border-2 border-slate-200 hover:border-blue-300 transition-colors">
              <CardContent className="p-6">
                <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <Bell className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="font-bold text-slate-900 mb-2">Lead Notifications</h3>
                <p className="text-sm text-slate-600 mb-3">
                  Know instantly when someone's interested.
                </p>
                <ul className="text-xs text-slate-500 space-y-1">
                  <li>✓ Email alerts for new leads</li>
                  <li>✓ See lead details in dashboard</li>
                  <li>✓ Track lead → customer journey</li>
                  <li>✓ Conversion notifications</li>
                </ul>
              </CardContent>
            </Card>

            {/* AI Marketing Tools */}
            <Card className="border-2 border-slate-200 hover:border-blue-300 transition-colors">
              <CardContent className="p-6">
                <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <Sparkles className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="font-bold text-slate-900 mb-2">AI Marketing Tools</h3>
                <p className="text-sm text-slate-600 mb-3">
                  Generate professional marketing content in seconds.
                </p>
                <ul className="text-xs text-slate-500 space-y-1">
                  <li>✓ Email templates</li>
                  <li>✓ Video scripts</li>
                  <li>✓ Social media posts</li>
                  <li>✓ Customized to your audience</li>
                </ul>
              </CardContent>
            </Card>

            {/* Email CRM */}
            <Card className="border-2 border-slate-200 hover:border-blue-300 transition-colors">
              <CardContent className="p-6">
                <div className="bg-slate-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <Mail className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="font-bold text-slate-900 mb-2">Email CRM Campaigns</h3>
                <p className="text-sm text-slate-600 mb-3">
                  Automate follow-ups to nurture your leads.
                </p>
                <ul className="text-xs text-slate-500 space-y-1">
                  <li>✓ Create email sequences</li>
                  <li>✓ Auto-send to new leads</li>
                  <li>✓ Track open rates</li>
                  <li>✓ Toggle campaigns on/off</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Industry Templates Preview */}
          <div className="mt-12 bg-slate-50 rounded-2xl p-8">
            <h3 className="text-xl font-bold text-slate-900 mb-4 text-center">
              Landing Page Templates for Every Industry
            </h3>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { name: "Auto Dealers", icon: "🚗" },
                { name: "RV & Boat", icon: "🚐" },
                { name: "Restaurants", icon: "🍽️" },
                { name: "Home Services", icon: "🔧" },
                { name: "Professional", icon: "💼" },
                { name: "Real Estate", icon: "🏠" },
                { name: "Healthcare", icon: "🏥" },
                { name: "Local Retail", icon: "🛍️" }
              ].map((industry) => (
                <span 
                  key={industry.name}
                  className="px-4 py-2 bg-white rounded-full border border-slate-200 text-sm text-slate-700 flex items-center gap-2"
                >
                  <span>{industry.icon}</span>
                  {industry.name}
                </span>
              ))}
            </div>
            <p className="text-center text-slate-500 text-sm mt-4">
              Pick your niche, customize the messaging, and start capturing leads!
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-blue-100 text-blue-700 font-bold">🦥 Why Lazy People Love Us</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              The Anti-Hustle Affiliate Program
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              No complicated funnels. No "guru" courses to buy. No splitting your hard-earned cash with middlemen platforms. 
              Just honest income for honest recommendations.
            </p>
          </div>
          
          {/* Comparison Section */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <Card className="border-2 border-red-200 bg-red-50/50">
              <CardHeader>
                <CardTitle className="text-red-700 flex items-center gap-2">
                  <span className="text-2xl">😤</span> Other Affiliate Programs
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="flex items-center gap-2 text-slate-600"><span className="text-red-500">✗</span> Monthly fees just to be a member</p>
                <p className="flex items-center gap-2 text-slate-600"><span className="text-red-500">✗</span> Platform takes a cut of YOUR commissions</p>
                <p className="flex items-center gap-2 text-slate-600"><span className="text-red-500">✗</span> Confusing tier systems and hidden rules</p>
                <p className="flex items-center gap-2 text-slate-600"><span className="text-red-500">✗</span> Low-quality products you'd be embarrassed to sell</p>
                <p className="flex items-center gap-2 text-slate-600"><span className="text-red-500">✗</span> "Gurus" who make more from courses than the product</p>
                <p className="flex items-center gap-2 text-slate-600"><span className="text-red-500">✗</span> Requires actual pants and a commute</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-blue-300 bg-blue-50/50">
              <CardHeader>
                <CardTitle className="text-blue-700 flex items-center gap-2">
                  <span className="text-2xl">🦥</span> Our Sloth-Approved Program
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="flex items-center gap-2 text-slate-700"><span className="text-blue-500 font-bold">✓</span> <strong>$0 to join, $0 forever</strong></p>
                <p className="flex items-center gap-2 text-slate-700"><span className="text-blue-500 font-bold">✓</span> Keep <strong>100%</strong> of your commission</p>
                <p className="flex items-center gap-2 text-slate-700"><span className="text-blue-500 font-bold">✓</span> Simple tiers: refer more, earn more</p>
                <p className="flex items-center gap-2 text-slate-700"><span className="text-blue-500 font-bold">✓</span> Premium websites you'd proudly recommend</p>
                <p className="flex items-center gap-2 text-slate-700"><span className="text-blue-500 font-bold">✓</span> Real support from humans (not chatbots)</p>
                <p className="flex items-center gap-2 text-slate-700"><span className="text-blue-500 font-bold">✓</span> <strong>Work from bed in your PJ's</strong> (we won't judge)</p>
              </CardContent>
            </Card>
          </div>
          
          {/* Work From Home Fun Card */}
          <Card className="mb-16 bg-gradient-to-r from-slate-900 to-blue-900 text-white overflow-hidden">
            <div className="grid md:grid-cols-2">
              <CardContent className="p-8 flex flex-col justify-center">
                <h3 className="text-2xl font-bold mb-4">🛋️ Hate the Corporate Grind?</h3>
                <p className="text-slate-300 mb-4">
                  Ditch the morning commute, the soul-crushing meetings, and the dress code nobody asked for.
                </p>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-center gap-2">✨ Work from your couch (or bed, we don't judge)</li>
                  <li className="flex items-center gap-2">👔 Business attire = optional pajamas</li>
                  <li className="flex items-center gap-2">⏰ Your hours, your rules, your nap schedule</li>
                  <li className="flex items-center gap-2">🏠 Zero commute = more Netflix time</li>
                </ul>
              </CardContent>
              <div className="hidden md:block relative">
                <img 
                  src="https://images.unsplash.com/photo-1543060829-a0029874b174?auto=format&fit=crop&w=600&h=400&q=80"
                  alt="Smiling man working comfortably from home with laptop on couch"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-slate-900/50 to-transparent" />
                <div className="absolute bottom-4 left-4 bg-blue-600/90 backdrop-blur-sm rounded-lg px-4 py-2">
                  <p className="text-sm font-bold">Real affiliate. Real PJ's. Real income.</p>
                </div>
              </div>
            </div>
          </Card>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {benefits.map((benefit, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="mb-4">{benefit.icon}</div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{benefit.title}</h3>
                  <p className="text-slate-600 text-sm">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Commission Tiers */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-blue-100 text-blue-700">Commission Structure</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Your Commission Grows With You
            </h2>
            <p className="text-lg text-slate-600">
              Start earning immediately. Unlock higher rates as you refer more clients.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {commissionTiers.map((tier, idx) => (
              <Card 
                key={idx} 
                className={`hover:shadow-xl transition-all ${
                  tier.featured 
                    ? 'border-2 border-blue-500 shadow-lg scale-105' 
                    : 'border border-slate-200'
                }`}
              >
                <CardHeader className={`text-center ${tier.featured ? 'bg-blue-50' : ''}`}>
                  {tier.featured && (
                    <Badge className="mb-2 bg-blue-600 text-white mx-auto">{tier.badge || 'Top Earner'}</Badge>
                  )}
                  <CardTitle className="text-xl">{tier.tier}</CardTitle>
                  <CardDescription>{tier.range} referrals</CardDescription>
                </CardHeader>
                <CardContent className="text-center pt-4">
                  <p className={`text-4xl font-bold mb-2 ${
                    tier.featured ? 'text-blue-600' : 'text-slate-900'
                  }`}>
                    {tier.rate}
                  </p>
                  <p className="text-slate-500 text-sm">Commission Rate</p>
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <p className="text-lg font-semibold text-slate-900">{tier.perClient}</p>
                    <p className="text-slate-500 text-xs">per referral/month (website only)</p>
                  </div>
                  <div className="mt-2 pt-2 border-t border-dashed border-slate-200">
                    <p className="text-lg font-bold text-blue-600">{tier.maxPerClient}</p>
                    <p className="text-blue-500 text-xs">per referral/month (with add-ons)</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Service Packages - Commission Breakdown */}
          <div className="mt-16">
            <div className="text-center mb-10">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">
                Stack Your Earnings With Add-Ons
              </h3>
              <p className="text-slate-600">
                Clients rarely buy just a website. Every add-on increases YOUR recurring commission.
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <Card className="overflow-hidden border-2 border-slate-200">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-900 text-white">
                        <th className="px-6 py-4 text-left text-sm font-semibold">Service</th>
                        <th className="px-6 py-4 text-center text-sm font-semibold">Client Pays</th>
                        <th className="px-6 py-4 text-center text-sm font-semibold">Your Starter (10%)</th>
                        <th className="px-6 py-4 text-center text-sm font-semibold">Your Elite (25%)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {servicePackages.map((pkg, i) => (
                        <tr key={i} className={`border-b border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                          <td className="px-6 py-3 font-medium text-slate-900">{pkg.name}</td>
                          <td className="px-6 py-3 text-center text-slate-600">{pkg.price}</td>
                          <td className="px-6 py-3 text-center font-semibold text-slate-700">{pkg.commission10}/mo</td>
                          <td className="px-6 py-3 text-center font-bold text-blue-600">{pkg.commission25}/mo</td>
                        </tr>
                      ))}
                      <tr className="bg-blue-50 border-t-2 border-blue-200">
                        <td className="px-6 py-4 font-bold text-blue-900">Full Stack Client</td>
                        <td className="px-6 py-4 text-center font-bold text-blue-900">$507/mo</td>
                        <td className="px-6 py-4 text-center font-bold text-blue-700">$51/mo</td>
                        <td className="px-6 py-4 text-center font-bold text-blue-600 text-lg">$127/mo</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Card>

              <p className="text-center text-slate-500 text-sm mt-4">
                Plus one-time setup fee commissions on Website ($499), Google Suite ($250), and SEO ($199-$499)
              </p>
            </div>
          </div>

          {/* Dealership & Enterprise Commissions */}
          <div className="mt-16">
            <div className="text-center mb-10">
              <Badge className="mb-4 bg-blue-100 text-blue-700">High-Ticket Opportunities</Badge>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">
                Big Deals = Big Commissions
              </h3>
              <p className="text-slate-600">
                Car dealerships, RV dealers, and enterprise clients pay $2,500 - $15,000+ per project
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <Card className="border border-slate-200 hover:shadow-lg transition-all">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                  </div>
                  <h4 className="font-bold text-lg mb-1">Local Business</h4>
                  <p className="text-2xl font-bold text-slate-900">$299/mo</p>
                  <p className="text-sm text-slate-500 mb-3">+ add-ons up to $507/mo</p>
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-sm text-blue-700">Your Elite commission:</p>
                    <p className="text-xl font-bold text-blue-600">$75 - $127/mo</p>
                    <p className="text-xs text-blue-600">recurring per client</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-400 shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <Badge className="mb-3 bg-blue-600 text-white">Highest Earner</Badge>
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <DollarSign className="w-6 h-6 text-blue-600" />
                  </div>
                  <h4 className="font-bold text-lg mb-1">Car / RV Dealers</h4>
                  <p className="text-2xl font-bold text-slate-900">$2,500 - $15,000+</p>
                  <p className="text-sm text-slate-500 mb-3">website + app + XML feeds</p>
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-sm text-blue-700">Your Elite commission:</p>
                    <p className="text-xl font-bold text-blue-600">$625 - $3,750</p>
                    <p className="text-xs text-blue-600">per deal (one referral!)</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-slate-200 hover:shadow-lg transition-all">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Shield className="w-6 h-6 text-blue-600" />
                  </div>
                  <h4 className="font-bold text-lg mb-1">Enterprise</h4>
                  <p className="text-2xl font-bold text-slate-900">$2,999+</p>
                  <p className="text-sm text-slate-500 mb-3">custom integrations + AI</p>
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-sm text-blue-700">Your Elite commission:</p>
                    <p className="text-xl font-bold text-blue-600">$750+</p>
                    <p className="text-xs text-blue-600">per enterprise deal</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Earnings Potential Calculator */}
          <div className="mt-16">
            <div className="text-center mb-10">
              <Badge className="mb-4 bg-blue-100 text-blue-700">Earnings Potential</Badge>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">
                See What You Could Earn
              </h3>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
              {earningsExamples.map((ex, i) => (
                <Card key={i} className={`hover:shadow-lg transition-all ${i === 3 ? 'border-2 border-blue-500 shadow-lg' : 'border border-slate-200'}`}>
                  <CardContent className="p-5">
                    <Badge variant="outline" className="mb-3 text-xs">{ex.tier}</Badge>
                    <p className="text-sm text-slate-600 mb-3 min-h-[40px]">{ex.scenario}</p>
                    <div className="space-y-1">
                      <p className="text-2xl font-bold text-slate-900">{ex.monthly}<span className="text-sm font-normal text-slate-500">/mo</span></p>
                      <p className="text-sm text-blue-600 font-semibold">{ex.annual}/year</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-8 max-w-2xl mx-auto text-center">
              <div className="bg-slate-900 text-white rounded-2xl p-8">
                <p className="text-slate-400 text-sm mb-2">Top partner earning potential</p>
                <p className="text-4xl sm:text-5xl font-bold text-blue-400 mb-2">$6,350+/month</p>
                <p className="text-slate-400">$76,200+/year in recurring commissions alone</p>
                <p className="text-xs text-slate-500 mt-3">+ one-time dealer/enterprise commissions of $625 - $3,750 per deal</p>
                <p className="text-xs text-slate-500">+ 10% of all sub-affiliate commissions you recruit</p>
                <Link to="/affiliate" className="inline-block mt-4">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8">
                    Start Earning Today
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-blue-600 text-white">How It Works</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-8">
                Start Earning in 3 Simple Steps
              </h2>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Apply & Get Approved</h3>
                    <p className="text-slate-400">Submit your application. Qualified partners are approved within 24 hours.</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Share Your Unique Link</h3>
                    <p className="text-slate-400">Get your personalized referral link and marketing materials. Share with your audience.</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Earn Recurring Income</h3>
                    <p className="text-slate-400">Get paid monthly for as long as your referrals remain clients. It's that simple.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <img 
                src="https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=800&q=80"
                alt="Business professionals discussing partnership opportunities - UPost affiliate success"
                title="UPost Partner Success"
                className="rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-blue-100 text-blue-700">Partner Success Stories</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              What Our Partners Say
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-blue-500 fill-blue-500" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-6 italic">"{testimonial.quote}"</p>
                  <div className="flex items-center gap-3">
                    <img 
                      src={testimonial.image}
                      alt={`${testimonial.name} - UPost affiliate partner testimonial`}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-semibold text-slate-900">{testimonial.name}</p>
                      <p className="text-slate-500 text-sm">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Milestone Bonuses */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-100 text-blue-700">Bonus Rewards</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Milestone Bonuses
            </h2>
            <p className="text-lg text-slate-600">
              Hit milestones, earn instant cash bonuses paid within 48 hours
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            {[
              { referrals: 5, bonus: 25 },
              { referrals: 10, bonus: 50 },
              { referrals: 25, bonus: 125 },
              { referrals: 50, bonus: 250 },
              { referrals: 75, bonus: 375 },
              { referrals: 100, bonus: 500 }
            ].map((milestone, idx) => (
              <Card key={idx} className="text-center hover:shadow-md transition-shadow border border-slate-200">
                <CardContent className="p-4">
                  <Gift className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-blue-600">${milestone.bonus}</p>
                  <p className="text-xs text-slate-500">{milestone.referrals} referrals</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-600 to-blue-800">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 text-white">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Ready to Partner With Us?
            </h2>
            <p className="text-xl text-blue-100">
              Apply now and start earning within 24 hours
            </p>
          </div>

          <Card className="shadow-2xl">
            <CardHeader>
              <CardTitle className="text-2xl">Partner Application</CardTitle>
              <CardDescription>
                Fill out the form below. Qualified applicants are approved within 24 hours.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Full Name *</label>
                    <Input 
                      required 
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Email Address *</label>
                    <Input 
                      type="email" 
                      required 
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Your Niche/Industry *</label>
                    <select
                      required
                      value={formData.niche}
                      onChange={(e) => setFormData({...formData, niche: e.target.value})}
                      className="w-full px-4 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select your niche</option>
                      <option value="automotive">Automotive/Dealerships</option>
                      <option value="digital-marketing">Digital Marketing</option>
                      <option value="small-business">Small Business</option>
                      <option value="tech">Technology/SaaS</option>
                      <option value="consulting">Business Consulting</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Audience Size *</label>
                    <Input 
                      type="number"
                      required 
                      min="10000"
                      value={formData.followerCount}
                      onChange={(e) => setFormData({...formData, followerCount: e.target.value})}
                      placeholder="Less than 10,000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Website or Social Profile URL *</label>
                  <Input 
                    required 
                    value={formData.profileUrl}
                    onChange={(e) => setFormData({...formData, profileUrl: e.target.value})}
                    placeholder="https://..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">How will you promote our services? *</label>
                  <textarea
                    required
                    rows="3"
                    value={formData.promotionStrategy}
                    onChange={(e) => setFormData({...formData, promotionStrategy: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    placeholder="Describe your promotion strategy..."
                  ></textarea>
                </div>

                <div className="flex items-start gap-3">
                  <input 
                    type="checkbox"
                    required
                    checked={formData.acceptedTerms}
                    onChange={(e) => setFormData({...formData, acceptedTerms: e.target.checked})}
                    className="mt-1"
                  />
                  <label className="text-sm text-slate-600">
                    I have read and agree to the{' '}
                    <Link to="/affiliate/terms" className="text-blue-600 hover:underline">
                      Partner Program Terms & Conditions
                    </Link>
                  </label>
                </div>

                <div className="flex items-start gap-3 bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <input 
                    type="checkbox"
                    id="smsConsent"
                    checked={formData.smsConsent || false}
                    onChange={(e) => setFormData({...formData, smsConsent: e.target.checked})}
                    className="mt-1"
                  />
                  <label htmlFor="smsConsent" className="text-sm text-slate-700">
                    <strong>SMS Notifications (Optional):</strong> I consent to receive occasional SMS text messages from UPost regarding my affiliate account, commission updates, and important announcements. Message frequency varies. Message and data rates may apply. Reply STOP to unsubscribe at any time.{' '}
                    <Link to="/sms-terms" className="text-blue-600 hover:underline">SMS Terms</Link> | <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>
                  </label>
                </div>

                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg font-semibold"
                >
                  {submitting ? 'Submitting...' : 'Submit Application'}
                </Button>

                <p className="text-center text-sm text-slate-500">
                  Qualified applicants are typically approved within 24 hours
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Frequently Asked Questions</h2>
          </div>

          <div className="space-y-4">
            {[
              {
                q: "Who can join the partner program?",
                a: "We welcome marketers, bloggers, consultants, agencies, and influencers with at least 10,000 engaged followers or a relevant audience in tech, business, automotive, or marketing niches."
              },
              {
                q: "How and when do I get paid?",
                a: "Commission payouts are processed monthly via Stripe. Milestone bonuses are paid within 48 hours of achievement. You'll receive a detailed earnings report in your partner dashboard."
              },
              {
                q: "What marketing materials do you provide?",
                a: "Approved partners get access to banners, email templates, social media graphics, landing page templates, and a comprehensive media kit. All designed to convert."
              },
              {
                q: "How long does the cookie last?",
                a: "Our tracking cookie lasts 90 days, giving you credit for conversions even if the prospect doesn't sign up immediately."
              }
            ].map((faq, idx) => (
              <Card key={idx} className="border border-slate-200">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                  <p className="text-slate-600">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Earning?</h2>
          <p className="text-xl text-slate-400 mb-8">
            Join successful partners earning $1,000+ per month in recurring commissions
          </p>
          <Button 
            size="lg" 
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8"
            onClick={() => document.getElementById('apply').scrollIntoView({ behavior: 'smooth' })}
          >
            Apply Now <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

    </div>
  );
}

export default AffiliateLanding;
